import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '@/components/common/toast';
import { TextField } from '@/components/forms/TextField';
import { ApiError } from '@/api/client/abpError';
import type { TenantRtExperienceDto } from '@/api/types/tenants';
import { TabActions, TabShell } from './TabShell';
import { useTenantRtExperience, useUpdateRtExperience } from '../api';

type RtForm = Record<string, string | boolean>;

const NUMBER_FIELDS: Array<{ name: keyof TenantRtExperienceDto; label: string; tip: string }> = [
  { name: 'refreshRatePriceMs', label: 'Refresh Rate Price (ms)', tip: 'Affects the Cash' },
  { name: 'refreshRateTradeMs', label: 'Refresh Rate Trade (ms)', tip: 'Affects the Cash' },
  { name: 'refreshRateEquityMs', label: 'Refresh Rate Equity (ms)', tip: 'Affects the Sigma page' },
  { name: 'refreshRateLpMs', label: 'Refresh Rate LP (ms)', tip: 'Affects the LP Accounts page' },
  { name: 'refreshRateNopMs', label: 'Refresh Rate NOP (ms)', tip: 'Affects the NOP page' },
];

// Legacy groups the sync toggles under named section headers (Equity Snapshot handled
// separately for the live/daily rule). Deal Comparison / Deal Surveillance have no rate.
const SYNC_GROUPS: Array<{ header: string; rows: Array<{ sync: keyof TenantRtExperienceDto; label: string; rate?: keyof TenantRtExperienceDto }> }> = [
  { header: 'AML', rows: [
    { sync: 'amlMoneyTransactionsSync', label: 'AML Money Transactions Sync', rate: 'refreshRateAmlMoneyTransactionsSec' },
    { sync: 'amlProfitThresholdSync', label: 'AML Profit Threshold Sync', rate: 'refreshRateAmlProfitThresholdSec' },
  ] },
  { header: 'Related Clients', rows: [{ sync: 'relatedClientsSync', label: 'Related Clients Sync', rate: 'refreshRateRelatedClientsSec' }] },
  { header: 'MT5 Api Deals', rows: [{ sync: 'mt5ApiDealsSync', label: 'MT5 API Deals Sync', rate: 'refreshRateMt5ApiDealsSec' }] },
  { header: 'LP Live Closed Trades & Deals', rows: [{ sync: 'lpLiveClosedTradesAndDealsSync', label: 'LP Live Closed Trades & Deals Sync', rate: 'refreshRateLpLiveClosedTradesAndDealsSec' }] },
  { header: 'LP Live Orders', rows: [{ sync: 'lpLiveOrdersSync', label: 'LP Live Orders Sync', rate: 'refreshRateLpLiveOrdersSec' }] },
  { header: 'Deal Comparison', rows: [{ sync: 'dealComparisonSync', label: 'Deal Comparison Sync' }] },
  { header: 'Trader Surveillance', rows: [{ sync: 'traderSurveillanceSync', label: 'Trader Surveillance Sync', rate: 'refreshRateTraderSurveillanceSec' }] },
  { header: 'Deal Surveillance', rows: [{ sync: 'dealSurveillanceSync', label: 'Deal Surveillance Sync' }] },
  { header: 'Feed Monitoring', rows: [{ sync: 'feedMonitoringSync', label: 'Feed Monitoring Sync', rate: 'refreshRateFeedMonitoringSec' }] },
  { header: 'Live Open Positions Snapshot', rows: [{ sync: 'liveOpenPositionsSnapshotSync', label: 'Live Open Positions Snapshot Sync' }] },
];

export function RtExperienceTab({ tenantId, onCancel }: { tenantId: string; onCancel: () => void }) {
  const toast = useToast();
  const rtQuery = useTenantRtExperience(tenantId);
  const updateMut = useUpdateRtExperience(tenantId);
  const { register, handleSubmit, reset, watch, setValue } = useForm<RtForm>();
  const liveEquityOn = Boolean(watch('liveEquitySnapshotSync'));

  useEffect(() => {
    const d = rtQuery.data;
    if (!d) return;
    const out: RtForm = {};
    Object.entries(d).forEach(([k, v]) => {
      out[k] = typeof v === 'boolean' ? v : String(v ?? '');
    });
    reset(out);
  }, [rtQuery.data, reset]);

  function onSubmit(values: RtForm) {
    const base = rtQuery.data;
    if (!base) return;
    const num = (k: keyof TenantRtExperienceDto) => Number(values[k as string] ?? 0);
    const bool = (k: keyof TenantRtExperienceDto) => Boolean(values[k as string]);

    const payload: TenantRtExperienceDto = {
      refreshRatePriceMs: num('refreshRatePriceMs'),
      refreshRateTradeMs: num('refreshRateTradeMs'),
      refreshRateEquityMs: num('refreshRateEquityMs'),
      refreshRateLpMs: num('refreshRateLpMs'),
      refreshRateNopMs: num('refreshRateNopMs'),
      refreshRateMt5ApiDealsSec: num('refreshRateMt5ApiDealsSec'),
      refreshRateLpLiveClosedTradesAndDealsSec: num('refreshRateLpLiveClosedTradesAndDealsSec'),
      refreshRateLpLiveOrdersSec: num('refreshRateLpLiveOrdersSec'),
      refreshRateAmlMoneyTransactionsSec: num('refreshRateAmlMoneyTransactionsSec'),
      refreshRateAmlProfitThresholdSec: num('refreshRateAmlProfitThresholdSec'),
      refreshRateRelatedClientsSec: num('refreshRateRelatedClientsSec'),
      refreshRateDealComparisonSec: num('refreshRateDealComparisonSec'),
      refreshRateTraderSurveillanceSec: num('refreshRateTraderSurveillanceSec'),
      refreshRateFeedMonitoringSec: num('refreshRateFeedMonitoringSec'),
      healthCheckDays: num('healthCheckDays'),
      swapsPercentageDifference: num('swapsPercentageDifference'),
      amlMoneyTransactionsSync: bool('amlMoneyTransactionsSync'),
      amlProfitThresholdSync: bool('amlProfitThresholdSync'),
      dealComparisonSync: bool('dealComparisonSync'),
      relatedClientsSync: bool('relatedClientsSync'),
      mt5ApiDealsSync: bool('mt5ApiDealsSync'),
      lpLiveClosedTradesAndDealsSync: bool('lpLiveClosedTradesAndDealsSync'),
      lpLiveOrdersSync: bool('lpLiveOrdersSync'),
      liveEquitySnapshotSync: bool('liveEquitySnapshotSync'),
      liveOpenPositionsSnapshotSync: bool('liveOpenPositionsSnapshotSync'),
      dailyEquitySnapshot: bool('dailyEquitySnapshot'),
      traderSurveillanceSync: bool('traderSurveillanceSync'),
      dealSurveillanceSync: bool('dealSurveillanceSync'),
      feedMonitoringSync: bool('feedMonitoringSync'),
    };
    updateMut.mutate(payload, {
      onSuccess: () => toast.success('RT experience settings saved.'),
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : 'Failed to save RT experience settings.'),
    });
  }

  return (
    <TabShell isLoading={rtQuery.isLoading} isError={rtQuery.isError} onRetry={() => rtQuery.refetch()}>
      <TabActions busy={updateMut.isPending} onCancel={onCancel} formId="rtExperienceForm" />
      <form id="rtExperienceForm" onSubmit={handleSubmit(onSubmit)} noValidate>
        <h4 className="mt-2 mb-3">
          <strong>Settings</strong>
        </h4>

        <h6>Live Refresh Rate</h6>
        <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-2 mb-3">
          {NUMBER_FIELDS.map((f) => (
            <div className="col" key={f.name}>
              <label className="form-label">
                {f.label} <i className="fa fa-info-circle text-muted" title={f.tip} />
              </label>
              <input className="form-control" type="number" {...register(f.name)} />
            </div>
          ))}
        </div>

        {SYNC_GROUPS.map((group) => (
          <div key={group.header}>
            <h6 className="fw-semibold border-top pt-2 mt-2">{group.header}</h6>
            <div className="row g-3 mb-2">
              {group.rows.map((row) => (
                <div className="col-md-6 col-lg-4" key={row.sync}>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch" id={row.sync} {...register(row.sync as keyof RtForm)} />
                    <label className="form-check-label" htmlFor={row.sync}>{row.label}</label>
                  </div>
                  {row.rate && <TextField label="Refresh Rate (sec)" type="number" {...register(row.rate)} />}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Equity Snapshot — can't run both live + daily; turning Live on disables/clears Daily. */}
        <h6 className="fw-semibold border-top pt-2 mt-2">Equity Snapshot</h6>
        <div className="row g-3 mb-2 align-items-start">
          <div className="col-md-6 col-lg-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="liveEquitySnapshotSync"
                {...register('liveEquitySnapshotSync', { onChange: (e) => { if (e.target.checked) setValue('dailyEquitySnapshot', false); } })}
              />
              <label className="form-check-label" htmlFor="liveEquitySnapshotSync">Live Equity Snapshot Sync</label>
            </div>
          </div>
          <div className="col-md-6 col-lg-4">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" role="switch" id="dailyEquitySnapshot" disabled={liveEquityOn} {...register('dailyEquitySnapshot')} />
              <label className="form-check-label" htmlFor="dailyEquitySnapshot">Daily Equity Snapshot</label>
            </div>
          </div>
          <div className="col-lg-4">
            <small className="text-muted">Equity Snapshot cannot be run in both live and daily mode. If set to live, the Daily Equity Snapshot option is automatically disabled.</small>
          </div>
        </div>

        <h6 className="fw-semibold border-top pt-2 mt-2">Health Check & Other</h6>
        <div className="row g-3">
          <div className="col-md-6 col-lg-4">
            <TextField
              label="Number of days to consider for health check"
              type="number"
              {...register('healthCheckDays')}
            />
          </div>
          <div className="col-md-6 col-lg-4">
            <TextField
              label="Swaps Percentage Difference"
              type="number"
              step="any"
              {...register('swapsPercentageDifference')}
            />
          </div>
        </div>
      </form>
    </TabShell>
  );
}
